import { useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line, RiCheckLine, RiDownloadLine, RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  ExternalLink,
  Flex,
  InfraCodeBlock,
  InfraDivider,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const PostgreSQLCDCInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';
  const publicIp = annotations['aws.infrastructure/public-ip'];
  const instanceId = annotations['aws.infrastructure/instance-id'];
  const region = annotations['aws.infrastructure/region'];

  const postgresVersion = annotations['postgres.infrastructure/version'];
  const postgresDatabase = annotations['postgres.infrastructure/database'];
  const postgresHost = annotations['postgres.infrastructure/host'];
  const postgresPort = annotations['postgres.infrastructure/port'];
  const postgresUser = annotations['postgres.infrastructure/user'];
  const postgresPassword = annotations['postgres.infrastructure/password'];
  const connectionString = annotations['postgres.infrastructure/connection-string'];

  const cdcEnabled = annotations['postgres.infrastructure/cdc-enabled'];
  const walLevel = annotations['postgres.infrastructure/wal-level'];
  const replicationSlot = annotations['postgres.infrastructure/replication-slot'];
  const publication = annotations['postgres.infrastructure/publication'];
  const sampleData = annotations['postgres.infrastructure/sample-data'];

  const sshPrivateKeyEncoded = annotations['postgres.infrastructure/ssh-private-key'];
  const sshCommand = annotations['postgres.infrastructure/ssh-command'];

  const hasPostgresCDCInfo = templateType === 'aws-postgres-cdc-source' && (postgresHost || publicIp);

  if (!hasPostgresCDCInfo) {
    return null;
  }

  const sshPrivateKey = useMemo(() => {
    if (!sshPrivateKeyEncoded) return '';
    try {
      return window.atob(sshPrivateKeyEncoded);
    } catch (e) {
      console.error('Failed to decode SSH key:', e);
      return '';
    }
  }, [sshPrivateKeyEncoded]);

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([sshPrivateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'postgres_key.pem';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const host = postgresHost || publicIp || 'Pending...';
  const port = postgresPort || '5432';
  const database = postgresDatabase || 'chinook';
  const user = postgresUser || 'rdi_user';

  const psqlCommand = `psql -h ${host} -p ${port} -U ${user} -d ${database}`;

  const verifySql = `-- Check WAL level
SHOW wal_level;

-- Check replication slot
SELECT * FROM pg_replication_slots WHERE slot_name = '${replicationSlot || 'rdi_slot'}';

-- Check publication
SELECT * FROM pg_publication WHERE pubname = '${publication || 'rdi_publication'}';`;

  const rdiConfig = `connection:
  type: postgresql
  host: ${host}
  port: ${port}
  database: ${database}
  user: ${user}
  password: ${postgresPassword || 'RedisCDC2024!'}
advanced:
  source:
    plugin.name: pgoutput
    publication.name: ${publication || 'rdi_publication'}
    slot.name: ${replicationSlot || 'rdi_slot'}
    snapshot.mode: initial`;

  return (
    <InfoCard title="PostgreSQL CDC Source Information" icon={<RiDatabase2Line size={20} />}>
      <InfraStack>
        <InfraSection title="🐘 PostgreSQL Configuration">
          {postgresVersion && (
            <InfraField
              label="Version:"
              value={<InfraTagValue>{`PostgreSQL ${postgresVersion}`}</InfraTagValue>}
              mono={false}
            />
          )}
          {cdcEnabled === 'true' && (
            <InfraField
              label="CDC Status:"
              value={
                <InfraTagRow>
                  <InfraTag>
                    <RiCheckLine size={14} style={{ marginRight: 4 }} />
                    Enabled
                  </InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {walLevel && (
            <InfraField label="WAL Level:" value={<InfraTagValue>{walLevel}</InfraTagValue>} mono={false} />
          )}
          {sampleData && (
            <InfraField
              label="Sample Data:"
              value={
                <InfraTagValue>
                  {sampleData === 'true' ? 'Chinook Database Loaded' : 'No Sample Data'}
                </InfraTagValue>
              }
              mono={false}
            />
          )}
        </InfraSection>

        <InfraSection title="🔌 Connection Information">
          <CopyableValue label="Host:" value={host} />
          <InfraField label="Port:" value={port} />
          <InfraField label="Database:" value={database} />
          <InfraField label="User:" value={user} />
          {postgresPassword && <CopyableValue label="Password:" value={postgresPassword} />}
          {connectionString && connectionString !== 'Pending deployment...' && (
            <>
              <InfraField label="Connection String:" value="" mono={false} />
              <div style={{ position: 'relative' }}>
                <InfraCodeBlock variant="light">{connectionString}</InfraCodeBlock>
                <div style={{ position: 'absolute', top: 4, right: 4 }}>
                  <CopyButton text={connectionString} />
                </div>
              </div>
            </>
          )}
          <InfraField label="psql Command:" value="" mono={false} />
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">{psqlCommand}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={psqlCommand} />
            </div>
          </div>
        </InfraSection>

        {sshPrivateKey && (
          <>
            <InfraDivider />
            <InfraSection title="🔐 SSH Access">
              {sshCommand && (
                <>
                  <InfraField label="SSH Command:" value="" mono={false} />
                  <div style={{ position: 'relative' }}>
                    <InfraCodeBlock variant="light">{sshCommand}</InfraCodeBlock>
                    <div style={{ position: 'absolute', top: 4, right: 4 }}>
                      <CopyButton text={sshCommand} />
                    </div>
                  </div>
                </>
              )}
              <InfraField label="SSH Private Key:" value="" mono={false} />
              <Flex gap="2">
                <Button variant="primary" size="small" onPress={downloadSSHKey}>
                  <RiDownloadLine size={16} />
                  Download Key
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                >
                  <RiFileCopyLine size={16} />
                  Copy Key
                </Button>
              </Flex>
              <Text variant="body-small" style={{ marginTop: '0.5rem', display: 'block' }}>
                💡 Save as postgres_key.pem and run: chmod 400 postgres_key.pem
              </Text>
            </InfraSection>
          </>
        )}

        <InfraSection title="🔄 CDC Configuration">
          {replicationSlot && (
            <InfraField label="Replication Slot:" value={replicationSlot} />
          )}
          {publication && <InfraField label="Publication:" value={publication} />}
          <InfraField label="Plugin:" value="pgoutput" />
          <Text variant="body-medium" style={{ marginTop: '1rem', display: 'block' }}>
            Verify CDC Configuration:
          </Text>
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">{verifySql}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={verifySql} />
            </div>
          </div>
        </InfraSection>

        <InfraDivider />
        <InfraSection title="🚀 RDI Configuration">
          <Text variant="body-medium">
            Use this configuration snippet for Redis Data Integration:
          </Text>
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">{rdiConfig}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={rdiConfig} />
            </div>
          </div>
          <Text variant="body-small" style={{ marginTop: '0.75rem', display: 'block' }}>
            💡 Tip: Replace &lt;your_password&gt; with the actual PostgreSQL password.
          </Text>
        </InfraSection>

        {instanceId && region && (
          <InfraSection title="AWS Console">
            <InfraField
              label="AWS Console:"
              value={
                <ExternalLink
                  href={`https://console.aws.amazon.com/ec2/v2/home?region=${region}#InstanceDetails:instanceId=${instanceId}`}
                >
                  View EC2 Instance
                </ExternalLink>
              }
              mono={false}
            />
          </InfraSection>
        )}

        <InfraDivider />
        <InfraSection title="📚 Documentation">
          <Text variant="body-medium">
            <ExternalLink href="https://redis.io/docs/latest/integrate/redis-data-integration/">
              Redis Data Integration Documentation
            </ExternalLink>
          </Text>
          <Text variant="body-medium">
            <ExternalLink href="https://debezium.io/documentation/reference/stable/connectors/postgresql.html">
              Debezium PostgreSQL Connector
            </ExternalLink>
          </Text>
          <Text variant="body-medium">
            <ExternalLink href="https://www.postgresql.org/docs/current/logical-replication.html">
              PostgreSQL Logical Replication
            </ExternalLink>
          </Text>
        </InfraSection>
      </InfraStack>
    </InfoCard>
  );
};
